import { InfoOutlined } from '@material-ui/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { CounterPartySelect } from '../../../components/Common/CounterPartySelection';
import CustomShowModal from '../../../components/Common/CustomShowModal';
import { getCurrentUserId } from '../../../utils';
import { getAddressDetails } from '../AddressBook/actions';
import { DownloadSustainabilityPdf } from './downloadPdf';

const safeNum = (val, fallback = 0) => {
    const num = parseFloat(val);
    return isNaN(num) || !isFinite(num) ? fallback : num;
};

const fuelData = [
    { type: "Diesel/Gas Oil", cf: 3.206 },
    { type: "Light Fuel Oil (LFO)", cf: 3.151 },
    { type: "Heavy Fuel Oil (HFO)", cf: 3.114 },
    { type: "Liquified Petroleum Gas (Propane)", cf: 3.000 },
    { type: "Liquified Petroleum Gas (Butane)", cf: 3.030 },
    { type: "Liquified Natural Gas", cf: 2.750 },
    { type: "Methanol", cf: 1.375 },
    { type: "Ethanol", cf: 1.913 }
];

const basicInfo = [
    ["Voyage No:", "text", "voyageNumber"],
    ["Vessel Name:", "text", "vesselName"],
    ["Vessel CP Date:", "date", "vesselCpDate"],
    ["Cargo Party:", "text", "cargoParty"],
    ["Cargo CP Date:", "date", "cargoCpDate"]
];

const SustainabilityModel = ({
    cargoDetails,
    calculatedExpenses,
    formData,
    setFormData
}) => {
    const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
    const [isOpenCargoModal, setIsOpenCargoModal] = useState(false);
    const [counterPartyDetails, setCounterPartyDetails] = useState([]);

    const fetchCounterPartyDetails = async () => {
        const userId = getCurrentUserId();
        try {
            const userRes = await getAddressDetails({
                userId,
                addressBookType: "user",
                perPage: 100,
            });

            setCounterPartyDetails(userRes?.data?.list);
        } catch (error) {
            console.error("Failed to fetch counter party details:", error);
            setCounterPartyDetails([]);
        }
    };

    useEffect(() => {
        fetchCounterPartyDetails();
    }, []);

    const handleCounterPartyChange = (id) => {
        setFormData(prev => ({
            ...prev,
            cargoPartyId: id
        }))
    }

    const getCfValue = (fuelType) => {
        const fuel = fuelData.find(f => f.type === fuelType);
        return fuel ? fuel.cf : "";
    };

    const calculateCarbonRows = () => {
        if (!calculatedExpenses?.travelDetails) return [];

        const getCfValueByType = (type) => {
            const row = (formData.fuelRows || []).find(r => r.type === type);
            return row ? getCfValue(row.selectedFuel) : 0;
        };

        const cfHSFO = safeNum(getCfValueByType("HFO"), 3.114);
        const cfLSFO = safeNum(getCfValueByType("ECA"), 3.151);
        const cfMGO = safeNum(getCfValueByType("MGO"), 3.206);

        return calculatedExpenses.travelDetails.map((item, index) => {
            const carbonRow = formData.carbonRows?.[index] || {};
            const phaseInPercent = safeNum(carbonRow.phaseInPercent, 40);
            const seaEtsPercent = safeNum(carbonRow.seaEtsPercent, 50);
            const portEtsPercent = safeNum(carbonRow.portEtsPercent, 100);
            const co2PricePerMT = safeNum(carbonRow.co2PricePerMT, 2.84);

            const hsfo = safeNum(item.HSFO);
            const lsfo = safeNum(item.LSFO);
            const mgo = safeNum(item.MGO);
            const stayHsfo = safeNum(item.stayHSFO);
            const stayLsfo = safeNum(item.stayLSFO);
            const stayMgo = safeNum(item.stayMGO);

            const totalEmission = (hsfo * cfHSFO) + (lsfo * cfLSFO) + (mgo * cfMGO)
                + (stayHsfo * cfHSFO) + (stayLsfo * cfLSFO) + (stayMgo * cfMGO);

            const seaEmission = (hsfo * cfHSFO) + (lsfo * cfLSFO) + (mgo * cfMGO);
            const seaEtsEmission = (phaseInPercent / 100) * seaEmission * (seaEtsPercent / 100);

            const portEmission = (stayHsfo * cfHSFO) + (stayLsfo * cfLSFO) + (stayMgo * cfMGO);
            const portEtsEmission = (phaseInPercent / 100) * portEmission * (portEtsPercent / 100);

            const totalEuEts = seaEtsEmission + portEtsEmission;
            const totalEuEtsExp = totalEuEts * co2PricePerMT;

            return {
                ...item,
                totalEmission: safeNum(totalEmission).toFixed(2),
                phaseInPercent,
                seaEmission: safeNum(seaEmission).toFixed(2),
                seaEtsPercent,
                seaEtsEmission: safeNum(seaEtsEmission).toFixed(2),
                portEmission: safeNum(portEmission).toFixed(2),
                portEtsPercent,
                portEtsEmission: safeNum(portEtsEmission).toFixed(2),
                totalEuEts: safeNum(totalEuEts).toFixed(2),
                co2PricePerMT,
                totalEuEtsExp: safeNum(totalEuEtsExp).toFixed(2)
            };
        });
    };

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            carbonRows: calculateCarbonRows()
        }));
    }, [calculatedExpenses, formData.fuelRows]);


    const handleCarbonChange = (rowIndex, field, value) => {
        const newRows = [...(formData.carbonRows || [])];
        if (!newRows[rowIndex]) return;

        const numericValue = safeNum(value);
        newRows[rowIndex][field] = numericValue;

        const row = newRows[rowIndex];
        const seaEmission = safeNum(row.seaEmission);
        const portEmission = safeNum(row.portEmission);
        const phaseIn = safeNum(row.phaseInPercent);
        const seaEtsPct = safeNum(row.seaEtsPercent);
        const portEtsPct = safeNum(row.portEtsPercent);
        const co2Price = safeNum(row.co2PricePerMT);

        const seaEtsEmission = (phaseIn / 100) * seaEmission * (seaEtsPct / 100);
        const portEtsEmission = (phaseIn / 100) * portEmission * (portEtsPct / 100);
        const totalEuEts = seaEtsEmission + portEtsEmission;
        const totalEuEtsExp = totalEuEts * co2Price;

        row.seaEtsEmission = safeNum(seaEtsEmission).toFixed(2);
        row.portEtsEmission = safeNum(portEtsEmission).toFixed(2);
        row.totalEuEts = safeNum(totalEuEts).toFixed(2);
        row.totalEuEtsExp = safeNum(totalEuEtsExp).toFixed(2);

        setFormData(prev => ({
            ...prev,
            carbonRows: newRows
        }));
    };

    function processCargoDetails(cargoDetails) {
        if (!cargoDetails || !Array.isArray(cargoDetails.cargoList)) {
            return {
                result: [],
                transportWork: 0,
                totalDistanceSailed: 0,
                totalFuelConsumed: 0,
                totalCarbonConsumed: "0.00",
                seaEtsEmission: "0.00",
                portEtsEmission: "0.00",
                totalEuEts: "0.00",
                totalEuEtsExp: "0.00",
                totalEmission: "0.00",
                eeoi: 0,
                totalCargoCarried: 0
            };
        }

        let result = [];
        let previousCargoOnShip = 0;

        cargoDetails.cargoList.forEach((item, index) => {
            const { operation, port, cargoDetails: cDetails = {}, cargoTx = 0 } = item;
            const distNormal = safeNum(cDetails.distNormal || cDetails.distNormalSystem);
            const distEca = safeNum(cDetails.distEca || cDetails.distEcaSystem);
            const totalDistance = distNormal; // distNormal already includes the ECA distance
            const parsedCargoTx = safeNum(cargoTx);

            let cargoOnShip = previousCargoOnShip;
            if (operation === "Loading") {
                cargoOnShip = previousCargoOnShip + parsedCargoTx;
            } else if (operation === "Discharging") {
                cargoOnShip = previousCargoOnShip - parsedCargoTx;
            }

            const tonneMiles = totalDistance * cargoOnShip;

            result.push({
                operation: operation,
                port: port,
                distNormal: safeNum(distNormal),
                distEca: safeNum(distEca),
                totalDistance: safeNum(totalDistance),
                cargoTx: safeNum(parsedCargoTx),
                cargoOnShip: safeNum(cargoOnShip),
                tonneMiles: safeNum(tonneMiles),
            });

            previousCargoOnShip = cargoOnShip;
        });

        const totalTravelHSFO = safeNum(calculatedExpenses?.totalBallastHSFO) + safeNum(calculatedExpenses?.totalLadenHSFO);
        const totalTravelLSFO = safeNum(calculatedExpenses?.totalBallastLSFO) + safeNum(calculatedExpenses?.totalLadenLSFO);
        const totalTravelMGO = safeNum(calculatedExpenses?.totalBallastMGO) + safeNum(calculatedExpenses?.totalLadenMGO);
        const totalStayHSFO = safeNum(calculatedExpenses?.totalBallastStayHSFO) + safeNum(calculatedExpenses?.totalLadenStayHSFO);
        const totalStayLSFO = safeNum(calculatedExpenses?.totalBallastStayLSFO) + safeNum(calculatedExpenses?.totalLadenStayLSFO);
        const totalStayMGO = safeNum(calculatedExpenses?.totalBallastStayMGO) + safeNum(calculatedExpenses?.totalLadenStayMGO);

        const carbonRows = Array.isArray(formData.carbonRows) ? formData.carbonRows : [];

        const totalEmission = carbonRows.reduce((total, row) => total + safeNum(row?.totalEmission), 0);
        const seaEtsEmission = carbonRows.reduce((total, row) => total + safeNum(row?.seaEtsEmission), 0);
        const portEtsEmission = carbonRows.reduce((total, row) => total + safeNum(row?.portEtsEmission), 0);
        const totalEuEts = carbonRows.reduce((total, row) => total + safeNum(row?.totalEuEts), 0);
        const totalEuEtsExp = carbonRows.reduce((total, row) => total + safeNum(row?.totalEuEtsExp), 0);

        const transportWork = result.reduce((acc, item) => acc + safeNum(item["tonneMiles"]), 0);
        const totalDistanceSailed = result.reduce((acc, item) => acc + safeNum(item["totalDistance"]), 0);
        const totalFuelConsumed = totalTravelHSFO + totalTravelLSFO + totalTravelMGO + totalStayHSFO + totalStayLSFO + totalStayMGO;
        const totalCargoCarried = result.filter(item => item?.operation?.toLowerCase() === "loading").reduce((acc, item) => acc + safeNum(item["cargoTx"]), 0);

        const eeoiRaw = transportWork > 0 ? (totalEmission * 1000000) / transportWork : 0;

        return {
            result,
            transportWork: safeNum(transportWork),
            totalDistanceSailed: safeNum(totalDistanceSailed),
            totalFuelConsumed: safeNum(totalFuelConsumed),
            totalCarbonConsumed: safeNum(totalEmission).toFixed(2),
            seaEtsEmission: safeNum(seaEtsEmission).toFixed(2),
            portEtsEmission: safeNum(portEtsEmission).toFixed(2),
            totalEuEts: safeNum(totalEuEts).toFixed(2),
            totalEuEtsExp: safeNum(totalEuEtsExp).toFixed(2),
            totalEmission: safeNum(totalEmission).toFixed(2),
            eeoi: safeNum(eeoiRaw),
            totalCargoCarried: safeNum(totalCargoCarried)
        };
    }

    const processedCargoDetails = processCargoDetails(cargoDetails);

    const handleDescriptionChange = (index, value) => {
        setFormData(prev => {
            const newFuelRows = [...(prev.fuelRows || [])];
            if (newFuelRows[index]) {
                newFuelRows[index].description = value;
            }
            return {
                ...prev,
                fuelRows: newFuelRows
            };
        });
    };

    const handleFuelChange = (index, value) => {
        setFormData(prev => {
            const newFuelRows = [...(prev.fuelRows || [])];
            if (newFuelRows[index]) {
                newFuelRows[index].selectedFuel = value;
            }
            return {
                ...prev,
                fuelRows: newFuelRows
            };
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const CounterPartyName = (cpId) => {
        const cp = (counterPartyDetails || []).find(cp => cp._id === cpId);
        return cp?.type === "Individual"
            ? `${cp.individual_title || ""} ${cp.individual_firstName || ""} ${cp.individual_middleName || ""} ${cp.individual_lastName || ""}`.trim()
            : cp?.organisation_name ||
            cp?.organisation_fullName ||
            `${cp?.organisation_firstName} ${cp?.organisation_lastName}` ||
            cp?.fullName ||
            "";
    }

    return (
        <div>
            <DownloadSustainabilityPdf formData={formData} processedCargoDetails={processedCargoDetails} cargoPartyName={CounterPartyName(formData.cargoPartyId)} />
            <div className="max-w-full mx-auto bg-white rounded-lg border border-slate-200 shadow-sm text-[11px] font-medium">
                {/* Top Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Left Section */}
                    <div className="lg:col-span-1 border-b lg:border-b-0 lg:border-r border-[#cbd5e1] p-1.5">
                        <div className="space-y-1">
                            {/* Basic Info Fields Table */}
                            <div className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden">
                                <table className="w-full border-collapse text-[11px] font-medium">
                                    <tbody className="divide-y-0 bg-white">
                                        {basicInfo.map(([label, type, key], idx) => {
                                            const isLast = idx === basicInfo.length - 1;
                                            const borderB = isLast ? "" : "border-b";
                                            return (
                                                <tr key={idx} className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                                    <td className={`px-2 py-0.5 font-medium text-slate-700 border-r ${borderB} border-[#cbd5e1] bg-white w-1/2 align-middle`}>
                                                        {label}
                                                    </td>
                                                    <td className={`px-2 py-0.5 font-medium text-slate-900 ${borderB} border-[#cbd5e1] bg-[#f0f4f9] w-1/2 align-middle`}>
                                                        {type === "date"
                                                            ? moment(formData[key]).isValid()
                                                                ? moment(formData[key]).format("DD/MM/YYYY")
                                                                : "N/A"
                                                            : key === "cargoParty" ? (
                                                                <button
                                                                    type="button"
                                                                    className="bg-transparent w-full text-left flex items-center justify-between cursor-pointer"
                                                                    onClick={() => setIsOpenCargoModal(true)}
                                                                >
                                                                    <span className={`block truncate ${formData.cargoPartyId ? "text-slate-800 font-medium" : "text-slate-500"} text-[11px]`}>
                                                                        {formData.cargoPartyId
                                                                            ? CounterPartyName(formData.cargoPartyId)
                                                                            : "Select a counterparty..."}
                                                                    </span>
                                                                    <FiChevronDown className="w-3.5 h-3.5 text-slate-700" />
                                                                </button>
                                                            ) : formData[key] || ""}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Fuel CF Selector Table */}
                            <div className="mt-1">
                                <p className="text-[11px] font-medium text-slate-800 my-0.5 mt-1.5 capitalize tracking-wide">
                                    Fuel CF Selector
                                </p>
                                <div className="overflow-x-auto rounded-none border border-[#cbd5e1] scroll-thin">
                                    <table className="w-full border-collapse text-[11px] font-medium min-w-[400px]">
                                        <thead className="bg-[#e2e8f0] text-[#1e293b] h-[24px]">
                                            <tr>
                                                <th className="px-2 whitespace-nowrap py-0.5 text-left border-r border-b border-[#cbd5e1] font-medium capitalize text-[11px] text-[#1e293b]">
                                                    Type
                                                </th>
                                                <th className="px-2 whitespace-nowrap py-0.5 text-left border-r border-b border-[#cbd5e1] font-medium capitalize text-[11px] text-[#1e293b]">
                                                    Description
                                                </th>
                                                <th className="px-2 whitespace-nowrap py-0.5 text-left border-r border-b border-[#cbd5e1] font-medium capitalize text-[11px] text-[#1e293b]">
                                                    <div className="flex items-center justify-between">
                                                        <span>MEPC Name</span>
                                                        <button onClick={() => setIsOpenDetailsModal(true)} type="button">
                                                            <InfoOutlined className="ml-1 text-sm text-slate-600 hover:text-slate-900" />
                                                        </button>
                                                    </div>
                                                </th>
                                                <th className="px-2 whitespace-nowrap py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-b border-[#cbd5e1]">
                                                    CF Value
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y-0 bg-white">
                                            {(formData.fuelRows || []).map((row, index) => {
                                                const isLast = index === (formData.fuelRows || []).length - 1;
                                                const borderB = isLast ? "" : "border-b";
                                                return (
                                                    <tr key={index} className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                                        <td className={`px-2 py-0 border-r ${borderB} border-[#cbd5e1] text-slate-900 font-medium text-[11px] bg-[#f0f4f9] align-middle`}>
                                                            {row.type}
                                                        </td>
                                                        <td className={`p-0 border-r ${borderB} border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle`}>
                                                            <input
                                                                type="text"
                                                                value={row.description || ""}
                                                                onChange={(e) =>
                                                                    handleDescriptionChange(index, e.target.value)
                                                                }
                                                                className="w-full h-full px-1 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                                            />
                                                        </td>
                                                        <td className={`p-0 border-r ${borderB} border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle`}>
                                                            <select
                                                                value={row.selectedFuel || ""}
                                                                onChange={(e) =>
                                                                    handleFuelChange(index, e.target.value)
                                                                }
                                                                className="w-full h-full px-1 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 cursor-pointer"
                                                            >
                                                                {fuelData.map((fuel) => (
                                                                    <option key={fuel.type} value={fuel.type}>
                                                                        {fuel.type}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className={`px-2 py-0 ${borderB} border-[#cbd5e1] bg-[#f0f4f9] text-slate-800 font-medium text-[11px] align-middle`}>
                                                            {getCfValue(row.selectedFuel)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Section - Summary Stats Table */}
                    <div className="lg:col-span-1 p-1.5">
                        <div className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden">
                            <table className="w-full border-collapse text-[11px] font-medium">
                                <tbody className="divide-y-0 bg-white">
                                    {[
                                        [
                                            "Total Cargo Carried",
                                            safeNum(processedCargoDetails?.totalCargoCarried).toFixed(2),
                                            "MT",
                                        ],
                                        [
                                            "Total Fuel Consumed",
                                            safeNum(processedCargoDetails?.totalFuelConsumed).toFixed(2),
                                            "MT",
                                        ],
                                        [
                                            "Total Distance Sailed",
                                            safeNum(processedCargoDetails?.totalDistanceSailed).toFixed(2),
                                            "NM",
                                        ],
                                        [
                                            "Total CO₂ Emitted",
                                            processedCargoDetails?.totalCarbonConsumed || "0.00",
                                            "MT",
                                        ],
                                        [
                                            "Transport Work",
                                            safeNum(processedCargoDetails?.transportWork).toFixed(2),
                                            "t-mi",
                                        ],
                                        [
                                            "EEOI",
                                            safeNum(processedCargoDetails?.eeoi).toFixed(2),
                                            "gCO₂/t-mi",
                                        ],
                                        [
                                            "Total ETS Cost",
                                            processedCargoDetails?.totalEuEtsExp || "0.00",
                                            "",
                                        ],
                                    ].map(([label, value, unit], i) => (
                                        <tr key={i} className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                            <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-b border-[#cbd5e1] bg-white w-1/2 align-middle text-[11px]">
                                                {label}
                                            </td>
                                            <td className="px-2 py-0.5 font-medium text-slate-900 border-b border-[#cbd5e1] bg-[#f0f4f9] w-1/2 text-left align-middle whitespace-nowrap text-[11px]">
                                                {value}{" "}
                                                <span className="text-slate-600 text-[11px] font-medium ml-1">{unit}</span>
                                            </td>
                                        </tr>
                                    ))}

                                    <tr className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                        <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-b border-[#cbd5e1] bg-white w-1/2 align-middle text-[11px]">
                                            Agreed ETS CO₂ (MT)
                                        </td>
                                        <td className="p-0 border-b border-[#cbd5e1] bg-[#ffffd6] w-1/2 align-middle h-[20px]">
                                            <input
                                                type="text"
                                                value={formData.agreedEtsCo2 || ''}
                                                onChange={(e) => handleInputChange('agreedEtsCo2', e.target.value)}
                                                className="w-full h-full px-2 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                            />
                                        </td>
                                    </tr>
                                    <tr className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                        <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-[#cbd5e1] bg-white w-1/2 align-middle text-[11px]">
                                            Agreed ETS Cost
                                        </td>
                                        <td className="p-0 bg-[#ffffd6] w-1/2 align-middle h-[20px]">
                                            <input
                                                type="text"
                                                value={formData.agreedEtsCost || ''}
                                                onChange={(e) => handleInputChange('agreedEtsCost', e.target.value)}
                                                className="w-full h-full px-2 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Pre/Post Ports */}
                <div className="p-1.5 border-b border-[#cbd5e1] bg-white">
                    <div className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden">
                        <table className="w-full border-collapse text-[11px] font-medium">
                            <tbody className="bg-white">
                                <tr className="h-[20px]">
                                    <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-[#cbd5e1] bg-white w-[25%] align-middle">
                                        Pre-Voyage Port of Call
                                    </td>
                                    <td className="p-0 border-r border-[#cbd5e1] bg-[#ffffd6] w-[25%] align-middle h-[20px]">
                                        <input
                                            type="text"
                                            value={formData.preVoyagePort || ''}
                                            onChange={(e) => handleInputChange('preVoyagePort', e.target.value)}
                                            className="w-full h-full px-2 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                        />
                                    </td>
                                    <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-[#cbd5e1] bg-white w-[25%] align-middle">
                                        Post-Voyage Port of Call
                                    </td>
                                    <td className="p-0 bg-[#ffffd6] w-[25%] align-middle h-[20px]">
                                        <input
                                            type="text"
                                            value={formData.postVoyagePort || ''}
                                            onChange={(e) => handleInputChange('postVoyagePort', e.target.value)}
                                            className="w-full h-full px-2 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Carbon Calculator Table */}
                <div className="p-1.5">
                    <h2 className="text-[11px] font-medium text-slate-800 mb-1 capitalize tracking-wide">
                        Carbon Emissions Analyzer
                    </h2>

                    <div className="overflow-x-auto border border-[#cbd5e1] rounded-none bg-white scroll-thin">
                        <table className="min-w-[900px] w-full border-collapse text-[11px] font-medium text-slate-800">
                            {/* ===== HEADER ===== */}
                            <thead className="bg-[#e2e8f0] text-[#1e293b]">
                                <tr>
                                    <th rowSpan={2} className="px-2 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        Port
                                    </th>
                                    <th rowSpan={2} className="px-2 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        Total Emission (MT)
                                    </th>
                                    <th rowSpan={2} className="px-2 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        Phase-In (%)
                                    </th>
                                    <th colSpan={3} className="px-2 py-1 text-center font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        Sea ETS
                                    </th>
                                    <th colSpan={3} className="px-2 py-1 text-center font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        Port ETS
                                    </th>
                                    <th rowSpan={2} className="px-2 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        Total EU ETS (MT)
                                    </th>
                                    <th rowSpan={2} className="px-2 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">
                                        CO₂ Price/MT
                                    </th>
                                    <th rowSpan={2} className="px-2 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-b border-[#cbd5e1] whitespace-nowrap">
                                        Total EU ETS Exp
                                    </th>
                                </tr>
                                <tr>
                                    <th className="px-2 py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">Sea Emission (MT)</th>
                                    <th className="px-2 py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">Sea ETS %</th>
                                    <th className="px-2 py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">Sea ETS Emission (MT)</th>
                                    <th className="px-2 py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">Port Emission (MT)</th>
                                    <th className="px-2 py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">Port ETS %</th>
                                    <th className="px-2 py-0.5 text-left font-medium capitalize text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] whitespace-nowrap">Port ETS Emission (MT)</th>
                                </tr>
                            </thead>

                            {/* ===== BODY ===== */}
                            <tbody className="divide-y-0 bg-white">
                                {(formData.carbonRows || []).map((row, i) => (
                                    <tr key={i} className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                        <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{row.port || "-"}</td>
                                        <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.totalEmission).toFixed(2)}</td>
                                        <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle">
                                            <input
                                                type="text"
                                                value={row.phaseInPercent ?? ''}
                                                onChange={(e) => handleCarbonChange(i, "phaseInPercent", e.target.value)}
                                                className="w-full h-full px-1.5 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                            />
                                        </td>
                                        <td className="px-2 py-0.5 text-slate-700 font-medium border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.seaEmission).toFixed(2)}</td>
                                        <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle">
                                            <input
                                                type="text"
                                                value={row.seaEtsPercent ?? ''}
                                                onChange={(e) => handleCarbonChange(i, "seaEtsPercent", e.target.value)}
                                                className="w-full h-full px-1.5 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                            />
                                        </td>
                                        <td className="px-2 py-0.5 text-slate-700 font-medium border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.seaEtsEmission).toFixed(2)}</td>
                                        <td className="px-2 py-0.5 text-slate-700 font-medium border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.portEmission).toFixed(2)}</td>
                                        <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle">
                                            <input
                                                type="text"
                                                value={row.portEtsPercent ?? ''}
                                                onChange={(e) => handleCarbonChange(i, "portEtsPercent", e.target.value)}
                                                className="w-full h-full px-1.5 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                            />
                                        </td>
                                        <td className="px-2 py-0.5 text-slate-700 font-medium border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.portEtsEmission).toFixed(2)}</td>
                                        <td className="px-2 py-0.5 font-medium text-slate-700 border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.totalEuEts).toFixed(2)}</td>
                                        <td className="p-0 border-r border-b border-[#cbd5e1] bg-[#ffffd6] h-[20px] align-middle">
                                            <input
                                                type="text"
                                                value={row.co2PricePerMT ?? ''}
                                                onChange={(e) => handleCarbonChange(i, "co2PricePerMT", e.target.value)}
                                                className="w-full h-full px-1.5 py-0 bg-transparent border-none outline-none text-[11px] font-medium text-slate-900 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                            />
                                        </td>
                                        <td className="px-2 py-0.5 font-medium text-slate-700 border-b border-[#cbd5e1] bg-[#f0f4f9]">{safeNum(row.totalEuEtsExp).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>

                            {/* ===== FOOTER ===== */}
                            <tfoot className="bg-[#f0f4f9] border-t border-[#cbd5e1] font-medium text-slate-700">
                                <tr>
                                    <td colSpan={1} className="px-2 py-1 border-r border-[#cbd5e1]"></td>
                                    <td className="px-2 py-1 text-slate-700 font-medium border-r border-[#cbd5e1]">{processedCargoDetails?.totalEmission || "0.00"}</td>
                                    <td colSpan={3} className="px-2 py-1 text-right capitalize tracking-wider text-[11px] text-slate-600 font-medium border-r border-[#cbd5e1]">Totals:</td>
                                    <td className="px-2 py-1 text-slate-700 font-medium border-r border-[#cbd5e1]">{processedCargoDetails?.seaEtsEmission || "0.00"}</td>
                                    <td colSpan={2} className="px-2 py-1 border-r border-[#cbd5e1]"></td>
                                    <td className="px-2 py-1 text-slate-700 font-medium border-r border-[#cbd5e1]">{processedCargoDetails?.portEtsEmission || "0.00"}</td>
                                    <td className="px-2 py-1 text-slate-700 font-medium border-r border-[#cbd5e1]">{processedCargoDetails?.totalEuEts || "0.00"}</td>
                                    <td className="px-2 py-1 border-r border-[#cbd5e1]"></td>
                                    <td className="px-2 py-1 text-[#313C4C] text-[11px] font-medium">{processedCargoDetails?.totalEuEtsExp || "0.00"}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                </div>
            </div>
            <CustomShowModal
                open={isOpenDetailsModal}
                handleClose={() => setIsOpenDetailsModal(false)}
                title="CF Factor Master Table"
                maxWidth="md"
            >
                <div className="p-2 sm:p-3 overflow-x-auto scroll-thin">
                    <h2 className="text-[11px] font-medium capitalize tracking-wider text-[#313C4C] mb-2">
                        Conversion Factor Between Fuel Consumption and CO2 Emission
                    </h2>
                    <div className="rounded-none border border-[#cbd5e1] overflow-hidden">
                        <table className="min-w-[650px] w-full border-collapse text-[11px] font-medium">
                            <thead className="bg-[#e2e8f0] text-[#1e293b] h-[24px]">
                                <tr>
                                    <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">#</th>
                                    <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Type of Fuel</th>
                                    <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Reference</th>
                                    <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Lower Calorific Value (KJ/Kg)</th>
                                    <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">Carbon Content</th>
                                    <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-b border-[#cbd5e1]">CF (t-CO₂/t-Fuel)</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y-0">
                                {[
                                    { type: "Diesel/Gas Oil", reference: "ISO 8217 Grades DMX through DMB", lcv: 42700, carbon: 0.8744, cf: 3.206 },
                                    { type: "Light Fuel Oil (LFO)", reference: "ISO 8217 Grades RMA through RMD", lcv: 41200, carbon: 0.8594, cf: 3.151 },
                                    { type: "Heavy Fuel Oil (HFO)", reference: "ISO 8217 Grades RME thorugh RMK", lcv: 40200, carbon: 0.8493, cf: 3.114 },
                                    { type: "Liquified Petroleum Gas (Propane)", reference: "Propane", lcv: 46300, carbon: 0.8182, cf: 3 },
                                    { type: "Liquified Petroleum Gas (Butane)", reference: "Butane", lcv: 45700, carbon: 0.8264, cf: 3.03 },
                                    { type: "Liquified Natural Gas", reference: "LNG", lcv: 48000, carbon: 0.75, cf: 2.75 },
                                    { type: "Methanol", reference: "Methanol", lcv: 19900, carbon: 0.375, cf: 1.375 },
                                    { type: "Ethanol", reference: "Ethanol", lcv: 26800, carbon: 0.5217, cf: 1.913 },
                                ].map((fuel, idx) => (
                                    <tr key={idx} className="h-[20px] hover:bg-amber-50/50 transition-colors">
                                        <td className="px-2.5 py-0.5 font-medium text-slate-700 border-r border-b border-[#cbd5e1] bg-[#f0f4f9]">{idx + 1}</td>
                                        <td className="px-2.5 py-0.5 font-medium text-slate-800 border-r border-b border-[#cbd5e1]">{fuel.type}</td>
                                        <td className="px-2.5 py-0.5 text-slate-600 font-medium border-r border-b border-[#cbd5e1]">{fuel.reference}</td>
                                        <td className="px-2.5 py-0.5 text-slate-600 font-medium border-r border-b border-[#cbd5e1]">{fuel.lcv}</td>
                                        <td className="px-2.5 py-0.5 text-slate-600 font-medium border-r border-b border-[#cbd5e1]">{fuel.carbon}</td>
                                        <td className="px-2.5 py-0.5 font-medium text-slate-900 border-b border-[#cbd5e1] bg-[#f0f4f9]">{fuel.cf}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </CustomShowModal>
            {isOpenCargoModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-5 border border-slate-200 animate-in fade-in duration-200 overflow-hidden">
                        <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-200 bg-[#313C4C] -mx-5 -mt-5 p-4 text-white">
                            <h2 className="text-[11px] font-medium text-white tracking-wide m-0">
                                Manage Counter Parties
                            </h2>
                            <button
                                className="text-white hover:bg-white/10 p-1 rounded-md transition"
                                onClick={() => setIsOpenCargoModal(null)}
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        <CounterPartySelect
                            value={formData.cargoPartyId}
                            onChange={(e) => handleCounterPartyChange(e.target.value)}
                            counterPartyDetails={counterPartyDetails}
                            isMultiSelect={false}
                            onCounterPartyAdded={fetchCounterPartyDetails}
                        />

                        {/* Footer */}
                        <div className="flex justify-end mt-4 pt-3 border-t border-slate-200">
                            <button
                                type="button"
                                className="px-5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 border border-slate-300 rounded-md transition active:scale-95 cursor-pointer"
                                onClick={() => setIsOpenCargoModal(false)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default SustainabilityModel;